import { ChildrenInformationPayload } from "@/actions/templates/ChildrenInformation/PostChildrenInformation";
import { dayjsLocal } from "./dayjsLocal";
import { formatPhoneNumber, parseLegacyAddress } from "@/utils/helpers";
import { format } from "@react-input/mask";

export const axiosBaseUrl = process.env.NEXT_PUBLIC_BACKEND_API_URL;
export const POSTHOG_HOST = "https://us.i.posthog.com";
export const GOOGLE_ADDRESS_API_KEY = "AIzaSyD-U5ZnygQUb0GXIxHbB1D0EYG3pg3foz0";
export const MINDSET_PHONE_NUMBER = "8888204270";
export const MINDSET_JAIL_PHONE_NUMBER = "8888599459";
export const MINDSET_JAIL_POST_RELEASE_PHONE_NUMBER = "8887894356";

export const META_DESCRIPTION = "Helping you apply for Social Security disability benefits.";

export const FULL_DATE_FORMAT = "M/DD/YYYY";
export const FULL_DATETIME_FORMAT = "MM/DD/YYYY h:mma";
export const DEFAULT_TASK_TYPE_ID = "2";

export const DOCUSIGN_COMPLETED_STATUS = "envelope-completed";

export const ARCHIVED_NOT_QUALIFIED_STATUS_ID = 93;

export const MEDICATIONS_API_URL = `https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search?sf=DISPLAY_NAME&df=DISPLAY_NAME&terms=`;

export const REQUIRED_FIELD = "This field is required.";
export const SERVER_DATE_FORMAT = "YYYY-MM-DD";
export const SERVER_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";
export const DISPLAY_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const HISTORY_TEMPLATES: {
  [key: string]: {
    title: string;
    columns: string[];
    defaultValues: any;
    endpoint: string;
    isLegacy?: boolean;
    cellFormatter: (record: any) => any[];
    editPayload: (record: any) => any;
  };
} = {
  SchoolHistory: {
    title: "Education History",
    endpoint: "SchoolHistory",
    columns: ["Type", "School Name", "End Date"],
    defaultValues: {
      Id: undefined,
      SchoolType: "",
      SchoolName: "",
      TeacherName: "",
      Address1: "",
      Address2: "",
      City: "",
      State: "",
      PostalCode: "",
      PhoneNumber: "",
      StartDate: null,
      EndDate: null,
      IsCurrentlyAttending: null,
      HighestGradeCompleted: "",
      IsGED: null,
      GedCertificateDate: null,
      IsHighSchoolDiploma: null,
      DegreesCertificatesLicenses: "",
      IsSpecialEducation: null,
      SpecialEducationStartGrade: "",
      SpecialEducationEndGrade: "",
      SpecialEducationExplanation: "",
      SchoolModifications: "",
    },
    cellFormatter: (record: any) => {
      const typeLabels: Record<string, string> = {
        K12: "K\u201312 Education",
        College: "College",
        TradeSchool: "Trade School",
        GraduateSchool: "Graduate School",
        Other: "Other",
      };
      return [
        typeLabels[record.schoolType] || record.schoolType,
        record.schoolName,
        record.isCurrentlyAttending
          ? "Present"
          : record.endDate
            ? dayjsLocal(record.endDate).format("MMM YYYY")
            : "",
      ];
    },
    editPayload: (record: any) => ({
      Id: record?.id,
      SchoolType: record?.schoolType,
      SchoolName: record?.schoolName,
      TeacherName: record?.teacherName ?? "",
      Address1: record?.address1,
      Address2: record?.address2,
      City: record?.city,
      State: record?.state,
      PostalCode: record?.postalCode,
      PhoneNumber: record?.phoneNumber ?? "",
      StartDate: record?.startDate ? dayjsLocal(record?.startDate).format("YYYY-MM-DD") : null,
      EndDate: record?.endDate ? dayjsLocal(record?.endDate).format("YYYY-MM-DD") : null,
      IsCurrentlyAttending: record?.isCurrentlyAttending ?? null,
      HighestGradeCompleted: record?.highestGradeCompleted,
      IsGED: record?.isGED,
      GedCertificateDate: record?.gedCertificateDate
        ? dayjsLocal(record?.gedCertificateDate).format("YYYY-MM-DD")
        : null,
      IsHighSchoolDiploma: record?.isHighSchoolDiploma,
      DegreesCertificatesLicenses: record?.degreesCertificatesLicenses,
      IsSpecialEducation: record?.isSpecialEducation,
      SpecialEducationStartGrade: record?.specialEducationStartGrade,
      SpecialEducationEndGrade: record?.specialEducationEndGrade,
      SpecialEducationExplanation: record?.specialEducationExplanation,
      SchoolModifications: record?.schoolModifications ?? "",
    }),
  },
  Education: {
    title: "Education History (Legacy)",
    isLegacy: true,
    endpoint: "EducationHistory",
    columns: ["High School", "Highest Grade", "GED/Diploma"],
    defaultValues: {
      Id: undefined,
      IsHighSchoolDiploma: false,
      IsGED: false,
      DateOfCertificate: null,
      LastHighSchoolAttended: "",
      HighSchoolCity: "",
      HighSchoolState: "",
      HighGradeCompleted: "",
      IsSpecialEducation: false,
      StartGrade: "",
      EndGrade: "",
    },
    cellFormatter: (record: any) => [
      record.lastHighSchoolAttended,
      record.highGradeCompleted,
      record.isHighSchoolDiploma ? "Diploma" : record.isGED ? "GED" : "N/A",
    ],
    editPayload: (record: any) => ({
      Id: record?.id,
      IsHighSchoolDiploma: record?.isHighSchoolDiploma,
      IsGED: record?.isGED,
      DateOfCertificate: record?.dateOfCertificate
        ? dayjsLocal(record?.dateOfCertificate).format("YYYY-MM-DD")
        : null,
      LastHighSchoolAttended: record?.lastHighSchoolAttended,
      HighSchoolCity: record?.highSchoolCity,
      HighSchoolState: record?.highSchoolState,
      HighGradeCompleted: record?.highGradeCompleted,
      IsSpecialEducation: record?.isSpecialEducation,
      StartGrade: record?.startGrade,
      EndGrade: record?.endGrade,
    }),
  },
  College: {
    title: "College History (Legacy)",
    isLegacy: true,
    endpoint: "CollegeHistory",
    columns: ["Name", "End Date"],
    defaultValues: {
      Id: undefined,
      CollegeName: "",
      Address: "",
      CollegeStartDate: null,
      CollegeEndDate: null,
      DegreesCertificateLicenses: "",
    },
    cellFormatter: (record: any) => [
      record.collegeName,
      record.collegeEndDate ? dayjsLocal(record.collegeEndDate).format("M/D/YY") : "",
    ],
    editPayload: (record: any) => ({
      Id: record?.id,
      CollegeName: record?.collegeName,
      Address: record?.address,
      CollegeStartDate: record?.collegeStartDate
        ? dayjsLocal(record?.collegeStartDate).format("YYYY-MM-DD")
        : null,
      CollegeEndDate: record?.collegeEndDate
        ? dayjsLocal(record?.collegeEndDate).format("YYYY-MM-DD")
        : null,
      DegreesCertificateLicenses: record?.degreesCertificateLicenses,
    }),
  },
  Work: {
    title: "Work History",
    endpoint: "WorkHistory",
    columns: ["Company Name", "Job Title", "Employed From", "Employed To"],
    defaultValues: {
      Id: undefined,
      CompanyName: "",

      JobTitle: "",
      IsCurrentlyWorking: undefined,
      IsSelfEmployed: undefined,
      EmployedFrom: null,
      EmployedTo: null,
      HoursPerDay: "",
      DaysPerWeek: "",
      PayAmount: "",
      PayFrequency: "",
      AddressTwo: "",
      Zip: "",
      City: "",
      State: "",
    },
    cellFormatter: (record: any) => [
      record.companyName,
      record.jobTitle,
      record.employedFrom ? dayjsLocal(record.employedFrom).format("MMMM YYYY") : "",
      record.employedTo ? dayjsLocal(record.employedTo).format("MMMM YYYY") : "",
    ],
    editPayload: (record: any) => {
      // Legacy records store full Google address in companyAddress
      let companyAddress = record?.companyAddress;
      let city = record?.city;
      let state = record?.state;
      if (!city && companyAddress) {
        const parsed = parseLegacyAddress(companyAddress);
        if (parsed) {
          companyAddress = parsed.street;
          city = parsed.city;
          state = parsed.state;
        }
      }
      return {
        Id: record?.id,
        CompanyName: record?.companyName,
        CompanyAddress: companyAddress,
        AddressTwo: record?.addressTwo,
        City: city,
        State: state,
        Zip: record?.zip,
        JobTitle: record?.jobTitle,
        EmployedFrom: record?.employedFrom
          ? dayjsLocal(record?.employedFrom).format("YYYY-MM-DD")
          : null,
        EmployedTo: record?.employedTo
          ? dayjsLocal(record?.employedTo).format("YYYY-MM-DD")
          : undefined,
        JobDuties: record?.jobDuties,
        IsCurrentlyWorking: record?.isCurrentlyWorking,
        HoursPerDay: record?.hoursPerDay,
        DaysPerWeek: record?.daysPerWeek,
        PayAmount: record?.payAmount,
        PayFrequency: record?.payFrequency,
        IsSelfEmployed: record?.isSelfEmployed,
      };
    },
  },
  Doctors: {
    title: "Doctor Visits",
    endpoint: "MedicalHistory",
    columns: ["Physician Name", "Facility Name", "Last Visit", "Reason for Visit"],
    defaultValues: {
      Id: undefined,
      DoctorName: "",
      DateOfVisit: null,
      ReasonForVisit: "",
      Diagnosis: "",
      AddressTwo: "",
      City: "",
      State: "",
    },
    cellFormatter: (record: any) => [
      record.physicianName,
      record.hospitalName,
      record.lastVisitDate ? dayjsLocal(record.lastVisitDate).format("M/D/YY") : "",
      record.medicationReason,
    ],
    editPayload: (record: any) => {
      // Legacy records store full Google address in physicianAddress
      let physicianAddress = record?.physicianAddress;
      let city = record?.city;
      let state = record?.state;
      if (!city && physicianAddress) {
        const parsed = parseLegacyAddress(physicianAddress);
        if (parsed) {
          physicianAddress = parsed.street;
          city = parsed.city;
          state = parsed.state;
        }
      }
      return {
        Id: record?.id,
        PhysicianName: record?.physicianName,
        TreatmentStartDate: record?.treatmentStartDate
          ? dayjsLocal(record?.treatmentStartDate).format("YYYY-MM-DD")
          : null,
        LastVisitDate: record?.lastVisitDate
          ? dayjsLocal(record?.lastVisitDate).format("YYYY-MM-DD")
          : null,
        NextScheduledVisitDate: record?.nextScheduledVisitDate
          ? dayjsLocal(record?.nextScheduledVisitDate).format("YYYY-MM-DD")
          : null,
        PhysicianAddress: physicianAddress,
        AddressTwo: record?.addressTwo,
        City: city,
        State: state,
        PhysicianPhoneNumber: format(record?.physicianPhoneNumber || "", PhoneNumberMask),
        MedicationReason: record?.medicationReason,
        PhysicianType: record?.physicianType,
        HospitalName: record?.hospitalName,
        Zip: record?.zip,
        FaxNumber: format(record?.faxNumber || "", PhoneNumberMask),
      };
    },
  },
  Medication: {
    title: "Medication History",
    endpoint: "MedicationDetails",
    columns: ["Prescription Name", "Physician Name", "Reason", "Start Date"],
    defaultValues: {
      Id: undefined,
      PhysicianId: undefined,
      MedicationId: undefined,
      PrescriptionDetails: "",
      PrescriptionStartDate: null,
    },
    cellFormatter: (record: any) => [
      record.prescriptionName,
      record.physicianName,
      record.prescriptionDetails,
      record.prescriptionStartDate
        ? dayjsLocal(record.prescriptionStartDate).format("M/D/YYYY")
        : "",
    ],
    editPayload: (record: any) => ({
      Id: record?.id,
      PhysicianId: record?.physicianId,
      MedicationName: record?.prescriptionName,
      MedicationId: record?.medicationId,
      PrescriptionDetails: record?.prescriptionDetails,
      PrescriptionStartDate: record?.prescriptionStartDate
        ? dayjsLocal(record?.prescriptionStartDate).format("YYYY-MM-DD")
        : "",
    }),
  },
  Marriage: {
    title: "Marriage History",
    endpoint: "MarriageInfo",
    columns: [
      "Spouse Name",
      "Marriage Date",
      // "Location",
      "Divorce Date",
      // "Spouse Date of Birth",
    ],
    defaultValues: {
      Id: undefined,
      CityOfMarriage: "",
      StateOfMarriage: "",
      CityOfDivorce: "",
      DateOfMarriage: null,
      DateOfDivorce: undefined,
      StateOfDivorce: "",
      SpouseName: "",
      SpouseDob: "",
      SpouseSSN: "",
      IsSpouseSsnUnknown: false,
      MarriageEndReason: null,
      IsPriorSpouseDeceased: null,
    },
    cellFormatter: (record: any) => [
      record.spouseName,
      record.dateOfMarriage ? dayjsLocal(record.dateOfMarriage).format("M/D/YY") : "",
      record.dateOfDivorce ? dayjsLocal(record.dateOfDivorce).format("M/D/YY") : "",
    ],
    editPayload: (record: any) => ({
      Id: record?.id,
      DateOfMarriage: record?.dateOfMarriage
        ? dayjsLocal(record?.dateOfMarriage).format("YYYY-MM-DD")
        : null,
      CityOfMarriage: record?.cityOfMarriage,
      StateOfMarriage: record?.stateOfMarriage,
      DateOfDivorce: record?.dateOfDivorce
        ? dayjsLocal(record?.dateOfDivorce).format("YYYY-MM-DD")
        : undefined,
      CityOfDivorce: record?.cityOfDivorce,
      StateOfDivorce: record?.stateOfDivorce,
      SpouseName: record?.spouseName,
      SpouseDob: record?.spouseDob ? dayjsLocal(record?.spouseDob).format("YYYY-MM-DD") : null,
      SpouseSSN: record?.spouseSSN,
      IsSpouseSsnUnknown: record?.isSpouseSsnUnknown ?? false,
      MarriageEndReason: record?.marriageEndReason ?? null,
      IsPriorSpouseDeceased: record?.isPriorSpouseDeceased ?? null,
    }),
  },
  Children: {
    title: "Children Information",
    endpoint: "ChildrenInformation",
    columns: ["First Name", "Last Name"],
    defaultValues: {
      Id: undefined,
      FirstName: "",
      MiddleName: "",
      LastName: "",
      IsCurrentlyMarried: undefined,
      IsDisabled: undefined,
    } as Partial<ChildrenInformationPayload>,
    cellFormatter: (record: any) => [record.firstName, record.lastName],
    editPayload: (record: any) => ({
      Id: record?.id,
      FirstName: record?.firstName,
      MiddleName: record?.middleName,
      LastName: record?.lastName,
      IsCurrentlyMarried: record?.isCurrentlyMarried,
      IsDisabled: record?.isDisabled,
    }),
  },
  Contacts: {
    title: "Your Contacts",
    endpoint: "EmergencyContact",
    columns: ["First Name", "Last Name", "Phone Number"],
    defaultValues: {
      Id: undefined,
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNumber: "",
      StreetAddress: "",
      City: "",
      State: "",
      Zip: "",
      Relationship: "",
    },
    cellFormatter: (record: any) => [
      record.firstName,
      record.lastName,
      formatPhoneNumber(record.phoneNumber),
    ],
    editPayload: (record: any) => ({
      Id: record?.id,
      FirstName: record?.firstName,
      LastName: record?.lastName,
      Email: record?.email,
      PhoneNumber: format(record?.phoneNumber || "", PhoneNumberMask),
      StreetAddress: record?.streetAddress,
      City: record?.city,
      State: record?.state,
      Zip: record?.zip,
      Relationship: record?.relationship,
    }),
  },
};

export const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "French",
  "Arabic",
  "Russian",
  "German",
  "Chinese",
  "Japanese",
  "Hindi",
  "Italian",
  "Others",
];

export const constants = {
  accessTokenKey: "accessToken",
  adminAccessTokenKey: "adminAccessToken", // Separate cookie for admin portal to avoid collision with claimant portal on localhost
  authTokenKey: "AuthToken",
  adminUserKey: "adminUser",
};

export const EVENT_LIBRARY = {
  SAVED_APPLICATION: "Saved Application",
  FORGOT_PASSWORD: "Forgot Password",
  LOGGED_IN: "Logged In",
  LOGGED_OUT: "Logged Out",
  LOGOUT_FAILED: "Logout Failed",
  RESET_PASSWORD: "Reset Password",
  SUBMITTED_LEAD: "Submitted Lead",
  SUBMITTED_PREQUAL: "Submitted Prequal",
  SUBMITTED_INTAKE: "Submitted Intake",
  INTAKE_SECTION: "Intake Section",
  REGISTERED: "Registered",
  ADDED_HISTORY: "Added History",
  UPDATED_HISTORY: "Updated History",
  DELETED_HISTORY: "Deleted History",
  SENT_MESSAGE: "Sent Message",
  UPLOADED_DOCUMENT: "Uploaded Document",
  DELETED_DOCUMENT: "Deleted Document",
  DOWNLOADED_DOCUMENT: "Downloaded Document",
  READ_NOTIFICATION: "Read Notification",
  UPDATED_PASSWORD: "Updated Password",
  UPDATED_NOTIFICATIONS: "Updated Notifications",
  ENCOUNTERED_ERROR: "Encountered Error",
  UPDATED_CONTACT_INFO: "Updated Contact Info",
  CREATED_SUPPORT_MESSAGE: "Created Support Message",
  REPLIED_SUPPORT_MESSAGE: "Replied to Support Message",
  CLOSED_SUPPORT_MESSAGE: "Closed Support Message",
};

export const ROUTE_PATHS = {
  ROOT: "/",
  LOGIN: "/login",
  LOGIN_AFTER_EXPIRED_LINK: "/login?isLinkExpired=true",
  TABLETLOGIN: "/tablet/login",
  REGISTER: "/secure-registration",
  PREQUALIFY: "/prequalify",
  FORGOT_PASSWORD: "/forgot",
  TABLET: {
    SPLASH: "/tablet/splash",
    VIAPATH_SSO_DENIAL: "/tablet/viaPathSsoDenail",
    VIAPATH_SSO_CONGRATS: "/viaPathSsoCongrats",
    VIAPATH_SSO_API_ROUTE: "/api/viaPathSso",
    PASSWORD_RESET_SECURITY_QUESTION: {
      CREATE: "/tablet/securityQuestions/create",
      ANSWER: "/tablet/securityQuestions/answer",
    },
  },
  INTAKE: {
    ROOT: "/intake",
    AGREE: "/intake/agree",
    DOCUSIGNINFO: "/intake/docusignInfo",
    STATUS: "/intake/status",
  },
  DASHBOARD: {
    HOME: "/dashboard/home",
    SET_PASSWORD: "/dashboard/set-password",
    STATUS: "/dashboard/status",
    HELP: "/dashboard/help",
    DOCUMENTS: "/dashboard/documents",
    NOTIFICATIONS: "/dashboard/notifications",
    MESSAGES: "/dashboard/messages",
    SETTINGS: "/dashboard/settings",
    APPLICATION: {
      ROOT: "/dashboard/application",
      BASIC: "/dashboard/application/basic",
      AGREEMENTS: "/dashboard/application/agreements",
      GENERAL: "/dashboard/application/general",
      MEDICAL: "/dashboard/application/medical",
      CONTACT: "/dashboard/application/contact",
      LANGUAGE: "/dashboard/application/language",
      FAMILY: "/dashboard/application/family",
      BENEFITS: "/dashboard/application/benefits",
      EMPLOYMENT: "/dashboard/application/employment",
      IDENTITY: "/dashboard/application/identity",
    },
    HISTORY: {
      ROOT: "/dashboard/history",
      EDUCATION: "/dashboard/history/education",
      COLLEGE: "/dashboard/history/college",
      SCHOOL: "/dashboard/history/school",
      WORK: "/dashboard/history/work",
      DOCTORS: "/dashboard/history/doctors",
      MEDICATION: "/dashboard/history/medication",
      MARRIAGE: "/dashboard/history/marriage",
      CHILDREN: "/dashboard/history/children",
      EMERGENCY: "/dashboard/history/emergency",
    },
  },
};

export const STR_ARR_DELIMITER = ";;;";

export const INTAKE_SYMPTOMS: {
  [key: string]: string[];
} = {
  Anxiety: [
    "Excessive worry",
    "Irritability",
    "Difficulty concentrating",
    "Panic attacks",
    "Difficulty sleeping",
    "Anticipating the worst",
  ],
  Depression: [
    "Feelings of hopelessness or worthlessness",
    "Loss of interest",
    "Loss of energy",
    "Irritability",
    "Negative or suicidal thoughts",
    "Difficulty concentrating",
    "Difficulty sleeping",
    "Avoiding friends, family, and activities.",
  ],
  Bipolar: [
    "Feeling euphoric, excessively happy",
    "More sociable and talkative",
    "Decreased need for sleep",
    "Racing thoughts",
    "Impulsive or risky behavior",
    "Loss of interest",
    "Feeling hopeless, empty, or down most of the time",
    "Insomnia",
    "Feelings of worthlessness or guilt",
    "Difficulty concentrating",
  ],
  "Post Traumatic Stress Disorder": [
    "Flashbacks",
    "Nightmares",
    "Intrusive thoughts",
    "Avoiding places, people, and activities",
    "Feeling hopeless, guilty, ashamed, or worthless",
    "Loss of interest in activities",
    "Hypervigilance",
    "Irritability or angry outbursts",
    "Difficulty concentrating",
    "Trouble falling asleep, staying asleep, or experiencing restless sleep",
  ],
  Schizophrenia: [
    "Hallucinations",
    "Paranoid delusions",
    "Erratic, unpredictable, or excessive movement",
    "Avoiding social interactions and isolating",
    "Struggling to focus or concentrate on tasks",
  ],
  ADHD: [
    "Trouble staying focused on tasks or activities",
    "Forgetfulness",
    "Difficulty organizing tasks and activities",
    "Easily distracted",
    "Fidgeting or squirming",
    "Excessive talking",
    "Impatience",
  ],
};

/* Client intake section order */
export const intake_sections = [
  "finances",
  "benefits",
  "background",
  "disabilities",
  "workpotential",
  "extra",
];

export const SSNMask = {
  mask: "___-__-____",
  replacement: { _: /\d/ },
};

export const PhoneNumberMask = {
  mask: "(___) ___-____",
  replacement: { _: /\d/ },
};

export const JAIL_RESTRICTED_FIELDS = ["Input", "Textarea"];

export const SPECIFIC_DOCUMENT_TYPES = {
  Unknown: 0,
  CustomerUploadedDocument: 1,
  SignedHipaaReleaseAuthorization: 2,
  SSA1695Form: 3,
  SSA1696Form: 4,
  MedicalRecords: 5,
  EducationRecords: 6,
  W2Forms: 7,
  CommunityLetter: 8,
  SSACorrespondence: 9,
  RepDocs: 10,
  EREDocuments: 11,
  ClaimantEvidence: 12,
  AdminUploadedDocument: 13,
  GeneratedDocument: 14,
  DocusignDocument: 15,
  DocusignCertificateOfCompletionSummary: 16,
  FastenHealthMedicalRecords: 110,
  // Delivered by Tavrn (Medical folder); stored by the integration, never chosen by hand.
  TavrnMedicalRecords: 117,
  TavrnNoRecordsStatement: 118,
};

export const DOCUMENT_CATEGORIES: {
  label: string;
  description?: string;
  value: number;
}[] = [
  {
    label: "Additional Documents",
    value: 3,
    description:
      "Any additional documents that were requested for your application can be uploaded here.",
  },
  {
    label: "SSA Correspondence",
    value: 4,
    description: "(SSA mail, DDS mail, OHO mail, applications, appeals)",
  },
  {
    label: "Medical/Education Records",
    value: 5,
  },
  {
    label: "Rep Docs",
    value: 6,
  },
  {
    label: "Claimant Supplied Evidence",
    value: 7,
  },
];

export type DocumentFilterField = "documentTypeId" | "specificDocumentTypeId";

export type AdminDocumentCategory = {
  label: string;
  description?: string;
  value: number;
  filterField: DocumentFilterField;
};

export const ADMIN_DOCUMENT_CATEGORIES: AdminDocumentCategory[] = [
  ...DOCUMENT_CATEGORIES.map((cat) => ({ ...cat, filterField: "documentTypeId" as const })),
  {
    label: "ERE Download",
    value: 8,
    filterField: "documentTypeId",
  },
];

// Categories available for user uploads (excludes system-only categories like Fasten Health)
export const UPLOADABLE_DOCUMENT_CATEGORIES: AdminDocumentCategory[] =
  ADMIN_DOCUMENT_CATEGORIES.filter(
    (cat) => cat.value !== SPECIFIC_DOCUMENT_TYPES.FastenHealthMedicalRecords
  );

// Specific document types that cannot be manually assigned by users
// These are populated only via system integrations (webhooks, etc.)
export const SYSTEM_ONLY_SPECIFIC_DOCUMENT_TYPES = [
  SPECIFIC_DOCUMENT_TYPES.FastenHealthMedicalRecords,
  SPECIFIC_DOCUMENT_TYPES.TavrnMedicalRecords,
  SPECIFIC_DOCUMENT_TYPES.TavrnNoRecordsStatement,
];

// Feature flag names - must match backend FeatureFlags values
export const FEATURE_FLAGS = {
  FastenHealth: "Fasten Health",
  MedicalRecordsRequests: "Medical Records Requests",
} as const;

export const appUserRoles = {
  superUser: "SuperAdmin",
  // Non-human integration accounts; mirrors SuperAdmin privileges (see lib/admin/permissions.ts).
  serviceUser: "ServiceUser",
  admin: "GeneralAdmin",
  leadAttorney: "LeadAttorney",
  caseManager: "CaseManager",
  customerSupportService: "CustomerSupportService",
  intakeMsr: "IntakeMSR",
  preQualMsr: "PreQualMSR",
  lawyer: "Lawyer",
};
export const supportUsers = [
  {
    id: 1,
    name: `Super Admin`,
    value: appUserRoles.superUser,
  },
  {
    id: 2,
    name: `General Admin`,
    value: appUserRoles.admin,
  },
  {
    id: 3,
    name: `Case Manager`,
    value: appUserRoles.caseManager,
  },
  {
    id: 4,
    name: `Customer Support Service`,
    value: appUserRoles.customerSupportService,
  },
  {
    id: 5,
    name: `Pre-Qual MSR`,
    value: appUserRoles.preQualMsr,
  },
  {
    id: 6,
    name: `Intake MSR`,
    value: appUserRoles.intakeMsr,
  },
  {
    id: 7,
    name: `Lawyer`,
    value: appUserRoles.lawyer,
  },
  {
    id: 8,
    name: `Lead Attorney`,
    value: appUserRoles.leadAttorney,
  },
  {
    id: 9,
    name: `Service User`,
    value: appUserRoles.serviceUser,
  },
];

/** Roles that carry SuperAdmin-level privilege: SuperAdmin itself and ServiceUser (integration accounts). */
export const SUPER_USER_ROLES: readonly string[] = [
  appUserRoles.superUser,
  appUserRoles.serviceUser,
];

/** True when `role` is SuperAdmin or a role that mirrors it. Use instead of `role === "SuperAdmin"`. */
export const isSuperUserRole = (role: string | null | undefined): boolean =>
  !!role && SUPER_USER_ROLES.includes(role);

/** True when any of a viewer's roles is SuperAdmin-level. Use instead of `userRoles?.includes("SuperAdmin")`. */
export const hasSuperUserRole = (userRoles: readonly string[] | null | undefined): boolean =>
  !!userRoles?.some(isSuperUserRole);

/**
 * True for staff-directory entries that are integration (non-human) accounts. They stay in the
 * directory so their notes/tasks resolve to a name, but must never be offered in assignment pickers.
 */
export const isServiceAccount = (
  user: { supportUserType?: string | null } | null | undefined
): boolean => user?.supportUserType === appUserRoles.serviceUser;

export const TIMEZONE_OPTIONS = [
  { label: "EST", value: "EST" },
  { label: "CST", value: "CST" },
  { label: "MST", value: "MST" },
  { label: "PST", value: "PST" },
  { label: "AKST", value: "AKST" },
  { label: "GMT", value: "GMT" },
];

/** Twin of NotQualifiedStatuses on the backend, which decides when a reason is kept or cleared. */
export const NOT_QUALIFIED_IDS = [4, 38, 73, 81, 93];

export const NOT_QUALIFIED_REASONS = [
  {
    id: 1,
    name: "Not Accepted by Mindset",
  },
  {
    id: 2,
    name: "Released Prior to Registration",
  },
  {
    id: 3,
    name: "Longterm Sentence",
  },
  {
    id: 4,
    name: "Already Has Representation",
  },
  {
    id: 5,
    name: "Transferred to another facility",
  },
  {
    id: 6,
    name: "No show",
  },
  {
    id: 7,
    name: "Rescheduled",
  },
  {
    id: 8,
    name: "Already Receiving Benefits",
  },
  {
    id: 9,
    name: "Above Age Limit",
  },
  {
    id: 10,
    name: "Weak Case/Lack of Treatment",
  },
  {
    id: 11,
    name: "Changed Mind/Not Interested",
  },
  {
    id: 12,
    name: "Looking for SSA",
  },
  {
    id: 13,
    name: "Over Income Limit",
  },
  {
    id: 14,
    name: "Active Claim Pending at Appeals Council (AC)",
  },
];

// Excludes Legacy
export const PIPELINE_ORDER = [
  "Intake/Main",
  "Spanish",
  "Partners - Correctional",
  "Partners - Community",
  "Partners - NOSSCR",
  "Self - Serve",
  "Tablet Users",
  "Intake Data",
  "Onboarding",
  "Claim Pending",
  "Hearing",
  "Post-Hearing",
  "Fees Pipeline",
  "Archived",
];

// Will eventually move to backend

export const CaseLevels = {
  IntakeSubmitted: "IntakeSubmitted",
  IntakeAccepted: "IntakeAccepted",
  AgreementsCompleted: "AgreementsCompleted",
  IntakeIncomplete: "IntakeIncomplete",
  Initial: "Initial",
  PreReceipt: "PreReceipt",
  AssignmentInProgress: "AssignmentInProgress",
  Assigned: "Assigned",
  Staging: "Staging",
  Reconsideration: "Reconsideration",
  ClosureInProgress: "ClosureInProgress",
  Closed: "Closed",
  PendingAnalystAssignment: "PendingAnalystAssignment",
  CaseWorkup: "CaseWorkup",
  AssignedToAnalyst: "AssignedToAnalyst",
  AssignedToAdjudicators: "AssignedToAdjudicators",
  NewCase: "NewCase",
  CasePermanentlyTransferred: "CasePermanentlyTransferred",
  DocumentPreparation: "DocumentPreparation",
  CaseClosed: "CaseClosed",
  DecisionWritingProcess: "DecisionWritingProcess",
  FolderAssembly: "FolderAssembly",
  InReview: "InReview",
  MasterDocket: "MasterDocket",
  PendingDecisionWriting: "PendingDecisionWriting",
  PendingFolderAssembly: "PendingFolderAssembly",
  PendingReview: "PendingReview",
  PostHearingDevelopment: "PostHearingDevelopment",
  PostHearingReview: "PostHearingReview",
  PreHearingDevelopment: "PreHearingDevelopment",
  PreHearingReview: "PreHearingReview",
  ReadyToSchedule: "ReadyToSchedule",
  ScheduledHearing: "ScheduledHearing",
} as const;

export type CaseLevelType = (typeof CaseLevels)[keyof typeof CaseLevels];

export type ClaimStatus = (typeof CLAIMS_STATUSES)[number];

export const CLAIMS_STATUSES = [
  {
    title: "Intake Submitted",
    description: "Your intake information has been submitted successfully.",
    body: "Our team is determining your eligibility for benefits. In the meantime, you can continue with your application until we've reached a decision. The more information you are able to provide, the faster we can make a determination on your eligibility.",
    stage: "intake",
    caseLevel: CaseLevels.IntakeSubmitted,
  },
  {
    title: "Intake Accepted",
    description: "Your intake information has been approved.",
    body: "Congratulations! Your intake information has been approved. Our team will reach out to you shortly to discuss the next steps in your application process.",
    stage: "intake",
    caseLevel: CaseLevels.IntakeAccepted,
  },
  {
    title: "Agreements Signed",
    description: "You have successfully signed agreements via Docusign.",
    body: "You have signed your agreements with Mindset, and a copy of these documents has been uploaded to the Rep Docs section of your Documents page.",
    stage: "intake",
    caseLevel: CaseLevels.AgreementsCompleted,
  },
  {
    title: "Collecting Data",
    description: "Please continue providing application information.",
    body: "We are gathering enough information to submit your initial application to SSA. Please continue providing application information so that we can submit the application on your behalf.",
    stage: "intake",
    caseLevel: CaseLevels.IntakeIncomplete,
  },
  {
    title: "Application Submitted",
    description:
      "SSA is reviewing your initial application to decide if you meet their requirements.",
    body: "The first step in the disability determination process where SSA reviews your initial application.",
    stage: "application",
    caseLevel: CaseLevels.Initial,
  },
  {
    title: "Pre-Receipt",
    description: "Your application is on its way to the state disability agency.",
    body: "SSA has sent your application to your state's disability agency (Disability Determination Services). Once the agency has recorded it, your application will be placed in line for a disability examiner.",
    stage: "application",
    caseLevel: CaseLevels.PreReceipt,
  },
  {
    title: "Staging",
    description: "Your case is in line to be assigned to a disability examiner.",
    body: "The state disability agency has received your forms, and the next step is for a disability examiner to review your file. They may contact you if they need more information or send additional forms.",
    stage: "application",
    caseLevel: CaseLevels.Staging,
  },
  {
    title: "Assignment in Progress",
    description: "Your case is being assigned to a disability examiner.",
    body: "The state disability agency has received your application and is assigning it to an examiner, who will review your medical records and work history.",
    stage: "application",
    caseLevel: CaseLevels.AssignmentInProgress,
  },
  {
    title: "Assigned",
    description: "Your case has been assigned to a disability examiner.",
    body: "The disability examiner will begin reviewing your application and medical information. They may contact you or your doctors if additional details are needed to help make a decision.",
    stage: "application",
    caseLevel: CaseLevels.Assigned,
  },
  {
    title: "Finalizing Decision",
    description: "SSA is finalizing the decision on your case.",
    body: "The state disability agency has completed its review of your case and is finalizing its decision. We will not know the outcome until SSA sends it. Your case manager will contact you as soon as the decision is received.",
    stage: "application",
    caseLevel: CaseLevels.ClosureInProgress,
  },
  {
    title: "Decision Received",
    description: "SSA has made a decision on your case.",
    body: "SSA has made a decision on your case. Your case manager will contact you as soon as possible to discuss next steps.",
    stage: "application",
    caseLevel: CaseLevels.Closed,
  },
  {
    title: "Reconsideration",
    description:
      "Your case is being looked at again by a different person because you disagreed with the first decision.",
    body: "A complete review of your claim by someone who did not take part in the first decision, usually requested after an initial denial.",
    stage: "application",
    caseLevel: CaseLevels.Reconsideration,
  },
  {
    title: "Master Docket",
    description: "Your case is on the list to be scheduled for a hearing.",
    body: "The scheduling list for upcoming hearings maintained by the hearing office.",
    stage: "hearing",
    caseLevel: CaseLevels.MasterDocket,
  },
  {
    title: "Transferred to Another Office",
    description: "Your case has been moved to a different office that will finish processing it.",
    body: "The case file has been moved to a different SSA office for handling and will not return to the original office.",
    stage: "hearing",
    caseLevel: CaseLevels.CasePermanentlyTransferred,
  },
  {
    title: "Pre Hearing Development",
    description: "SSA is collecting records before your hearing.",
    body: "Gathering medical and work-history evidence before your hearing.",
    stage: "hearing",
    caseLevel: CaseLevels.PreHearingDevelopment,
  },
  {
    title: "Pending Folder Assembly",
    description: "SSA is gathering and organizing your documents.",
    body: "Awaiting the organization of all case documents before the next step.",
    stage: "hearing",
    caseLevel: CaseLevels.PendingFolderAssembly,
  },
  {
    title: "Folder Assembly",
    description: "SSA is organizing your file so it's ready for review.",
    body: "Organizing all case materials into the official claim folder for review or hearing.",
    stage: "hearing",
    caseLevel: CaseLevels.FolderAssembly,
  },
  {
    title: "Pending Review",
    description: "Your case is in line to be reviewed.",
    body: "The case is waiting for a review by a decision-maker or quality control.",
    stage: "hearing",
    caseLevel: CaseLevels.PendingReview,
  },
  {
    title: "In Review",
    description: "SSA is double-checking your case before making a decision.",
    body: "The case is being reviewed by a decision-maker or quality reviewer.",
    stage: "hearing",
    caseLevel: CaseLevels.InReview,
  },
  {
    title: "Pre Hearing Review",
    description: "SSA is making sure your file is complete before your hearing.",
    body: "A pre-hearing check to ensure all evidence is complete and ready for the hearing.",
    stage: "hearing",
    caseLevel: CaseLevels.PreHearingReview,
  },
  {
    title: "Ready To Schedule",
    description: "Your case is ready to be scheduled for a hearing.",
    body: "The case is complete and ready to be placed on the hearing schedule.",
    stage: "hearing",
    caseLevel: CaseLevels.ReadyToSchedule,
  },
  {
    title: "Scheduled Hearing",
    description: "Your hearing date has been scheduled.",
    body: "A hearing date has been officially set.",
    stage: "hearing",
    caseLevel: CaseLevels.ScheduledHearing,
  },
  {
    title: "Post Hearing Development",
    description: "SSA is collecting more information after your hearing before making a decision.",
    body: "Collecting additional evidence or documentation after a hearing before a decision is issued.",
    stage: "hearing",
    caseLevel: CaseLevels.PostHearingDevelopment,
  },
  {
    title: "Post Hearing Review",
    description: "SSA is reviewing everything from your hearing before finalizing the decision.",
    body: "Reviewing all evidence and hearing records after the hearing to prepare the decision.",
    stage: "hearing",
    caseLevel: CaseLevels.PostHearingReview,
  },
  {
    title: "Pending Decision Writing",
    description: "A decision has been made, but SSA is still writing the official notice.",
    body: "The case has been decided but the written decision is not yet completed.",
    stage: "hearing",
    caseLevel: CaseLevels.PendingDecisionWriting,
  },
  {
    title: "Decision Writing Process",
    description: "SSA is writing up the official decision for your case.",
    body: "The step where the written decision is drafted based on findings from a hearing or case review.",
    stage: "hearing",
    caseLevel: CaseLevels.DecisionWritingProcess,
  },
  {
    title: "Decision Received",
    description: "SSA has issued a decision on your case.",
    body: "The hearing office has issued its decision on your case. Your case manager will contact you as soon as possible to go over it and discuss next steps, including any appeal deadline.",
    stage: "hearing",
    caseLevel: CaseLevels.CaseClosed,
  },
  {
    title: "New Case",
    description: "The Appeals Council has received your request for review.",
    body: "Your request for the Appeals Council to review the judge's decision has been received but has not yet been picked up for processing.",
    stage: "appeals",
    caseLevel: CaseLevels.NewCase,
  },
  {
    title: "Case Workup",
    description:
      "SSA is screening your request and getting your file ready for a specialist to review.",
    body: "Appeals Council staff are confirming your request was filed on time, gathering the hearing record and any new evidence, and handling any requests for more time before the case is placed in line for an analyst.",
    stage: "appeals",
    caseLevel: CaseLevels.CaseWorkup,
  },
  {
    title: "Pending Analyst Assignment",
    description: "Your case is waiting to be given to a specialist who will review it.",
    body: "The case is awaiting assignment to a claims analyst who will review the file and gather necessary evidence.",
    stage: "appeals",
    caseLevel: CaseLevels.PendingAnalystAssignment,
  },
  {
    title: "Assigned to Analyst",
    description: "A specialist is now reviewing your case.",
    body: "A claims analyst has been assigned and is responsible for reviewing and developing the case.",
    stage: "appeals",
    caseLevel: CaseLevels.AssignedToAnalyst,
  },
  {
    title: "Assigned to Adjudicator(s)",
    description: "Your case is with the person who will make the official decision.",
    body: "The claim has been assigned to an adjudicator who will issue the decision.",
    stage: "appeals",
    caseLevel: CaseLevels.AssignedToAdjudicators,
  },
  {
    title: "Document Preparation",
    description: "The Appeals Council has acted on your case and is preparing its notice.",
    body: "The Appeals Council has reached its decision on your request for review and is preparing the written notice to send to you. Your case manager will go over it with you as soon as it arrives.",
    stage: "appeals",
    caseLevel: CaseLevels.DocumentPreparation,
  },
  // Display only: CasePermanentlyTransferred is one enum value, and the hearing row above matches first.
  {
    title: "Transferred to Another Office",
    description: "Your case has been moved to a different office that will finish processing it.",
    body: "The case file has been moved to a different SSA office for handling and will not return to the original office.",
    stage: "appeals",
    caseLevel: CaseLevels.CasePermanentlyTransferred,
  },
  // {
  //   title: "Decision Received",
  //   description: "The Appeals Council has issued its decision on your case.",
  //   body: "The Appeals Council has issued its decision on your request for review. Your case manager will contact you as soon as possible to go over it and discuss next steps.",
  //   stage: "appeals",
  //   caseLevel: CaseLevels.CaseClosed,
  // },
];

// Case Management Partner Portal Constants
export const PARTNER_SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes
export const PARTNER_SESSION_WARNING_TIME = 14 * 60 * 1000; // Show warning at 14 minutes

export const PARTNER_ACTIVITY_TYPES = {
  LOGIN: 31,
  CREATED_CUSTOMER: 32,
  UPDATED_CUSTOMER: 33,
  DELETED_CUSTOMER: 34,
  SENT_REGISTRATION: 35,
  BULK_UPDATE: 36,
  BULK_DELETE: 37,
  BULK_SEND: 38,
  EXPORT_DATA: 39,
  IMPORT_DATA: 40,
  EDITED_PREQUALIFICATION: 41,
  PASSWORD_RESET: 42,
  TWO_FA_ENABLED: 43,
} as const;

export const PARTNER_BULK_OPERATION_LIMIT = 100;

// Education level options - Intake questionnaire values
export const INTAKE_EDUCATION_OPTIONS = [
  "Kindergarten",
  "1st grade",
  "2nd grade",
  "3rd grade",
  "4th grade",
  "5th grade",
  "6th grade",
  "7th grade",
  "8th grade",
  "9th grade",
  "10th grade",
  "11th grade",
  "12th grade",
  "High School Diploma or GED",
  "GED",
  "1 year of college",
  "2 years of college",
  "3 years of college",
  "College Degree (Associate's, Bachelor's)",
  "Advanced Degree (Master's, PhD, etc.)",
] as const;

// Education level options - Application form values (SSA format)
export const APPLICATION_EDUCATION_OPTIONS = [
  "None",
  "Kindergarten",
  "1st Grade",
  "2nd Grade",
  "3rd Grade",
  "4th Grade",
  "5th Grade",
  "6th Grade",
  "7th Grade",
  "8th Grade",
  "9th Grade",
  "10th Grade",
  "11th Grade",
  "12th Grade",
  "GED",
  "1 Year of College",
  "2 Years of College",
  "3 Years of College",
  "4 or More Years of College",
] as const;

export type IntakeEducationOption = (typeof INTAKE_EDUCATION_OPTIONS)[number];
export type ApplicationEducationOption = (typeof APPLICATION_EDUCATION_OPTIONS)[number];

// Type-safe mapping: TypeScript will error if any intake option is missing
export const INTAKE_TO_APPLICATION_EDUCATION_MAP: Record<
  IntakeEducationOption,
  ApplicationEducationOption
> = {
  Kindergarten: "Kindergarten",
  "1st grade": "1st Grade",
  "2nd grade": "2nd Grade",
  "3rd grade": "3rd Grade",
  "4th grade": "4th Grade",
  "5th grade": "5th Grade",
  "6th grade": "6th Grade",
  "7th grade": "7th Grade",
  "8th grade": "8th Grade",
  "9th grade": "9th Grade",
  "10th grade": "10th Grade",
  "11th grade": "11th Grade",
  "12th grade": "12th Grade",
  "High School Diploma or GED": "12th Grade",
  GED: "GED",
  "1 year of college": "1 Year of College",
  "2 years of college": "2 Years of College",
  "3 years of college": "3 Years of College",
  "College Degree (Associate's, Bachelor's)": "4 or More Years of College",
  "Advanced Degree (Master's, PhD, etc.)": "4 or More Years of College",
};

// Claim Types - mirrors backend ClaimType enum (Claims.cs)
export const CLAIM_TYPE = {
  SSI: 1,
  SSDI: 2,
} as const;

// Payment Types for Claim Fees
export const PAYMENT_TYPE = {
  SSI: "SSI",
  SSDI: "SSDI",
  SSDI_AUXILIARY: "SSDI Auxiliary",
} as const;
